/**
 * Renders a view's `grid` (cross-tab) representation from the engine's `GridData`
 * (`computeGridData`). Pure over the data model: a rows×cols label grid where each
 * cell LISTS the modules at that (row, col) intersection as chips; an empty cell
 * reads as empty. When the view carries a `color` getter, each chip carries a
 * color dot + low-alpha tint and a swatch legend renders in the footer.
 *
 * ## User flow (what the grid is FOR)
 * The grid answers "which modules sit at the intersection of two facets?" — e.g.
 * team × layer. The reader scans:
 *   1. the COLUMN header row (sticky at top) and ROW header column (sticky at
 *      left) to fix the two axes — both stay pinned while the body scrolls, so a
 *      crowded cross-tab never loses its coordinates;
 *   2. a CELL, whose chips list every module at that (row, col) — chips wrap, and
 *      a very crowded cell scrolls internally rather than blowing out the row;
 *   3. an EMPTY cell, which shows a muted "·" so "nothing here" is legible, not
 *      just blank;
 *   4. the color LEGEND (footer) when a `color` getter is present — each chip's
 *      dot + tint maps to a legend swatch, so the third facet is readable without
 *      leaving the grid.
 * Display-only by design: a cross-tab has no single cell-level write target.
 */
import { useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { useContextMenu, type ContextMenuItem } from "@archlang/ui";
import { paletteColor } from "@archlang/scene";
import { useTranslation } from "../i18n";
import { isGround } from "./viewFocus.js";
import { FOCUS_GROUND_ALPHA } from "@archlang/scene/board";
import { useWindow } from "./virtualization/windowing.js";
import { SpacerRow } from "./virtualization/Spacers.js";

/** Chips rendered per cell before a `+N more` reveal, and the bounded step each
 *  reveal adds — so an unbounded (thousands-deep) cell never mounts at once. */
const MEMBER_CAP = 50;
const MEMBER_STEP = 100;

/** One member module chip. */
export interface GridMemberVM {
  readonly key: string;
  readonly label: string;
}

/**
 * The render contract — structurally satisfied by the engine's `GridData`
 * (`computeGridData`). Kept local so the component doesn't couple to the engine
 * barrel (see ViewMatrixView / ViewTableView).
 */
export interface GridViewModel {
  readonly axisRows: readonly string[];
  readonly axisCols: readonly string[];
  readonly cells: readonly (readonly { readonly members: readonly GridMemberVM[] }[])[];
  /** Module key → color (empty when the view carries no `color` getter). */
  readonly colors: ReadonlyMap<string, string>;
  /** Color legend rows (empty when the view carries no `color` getter). */
  readonly legend: readonly { readonly label: string; readonly color?: string }[];
}

export interface ViewGridViewProps {
  readonly data: GridViewModel;
  /** The view's `focus` set (origin-qualified keys — the space `GridMember.key`
   *  lives in). Ground chips recede; the per-member `colors` channel is
   *  untouched underneath, so a chip still traces to its legend row. */
  readonly focus?: ReadonlySet<string>;
}

/** Chip tint is driven by a single CSS var so the stylesheet owns the alpha.
 *
 *  The assignment arrives as a palette NAME (`colorize()` hashes into
 *  `SWATCH_RING`; the engine holds no hex), so it resolves here — the live peer of
 *  the resolution `compose-grid` does for the export. Handing CSS the raw name
 *  would tint with the KEYWORD blue while the board and the export paint this
 *  theme's, i.e. one model value reading as two colours. */
function chipStyle(color: string | undefined): CSSProperties | undefined {
  return color ? ({ "--arch-chip-color": paletteColor(color) } as CSSProperties) : undefined;
}

/**
 * One (row, col) cell. Members are the grid's one UNBOUNDED axis, so a cell caps
 * its chips at {@link MEMBER_CAP} and reveals the rest in bounded {@link
 * MEMBER_STEP} steps via a `+N more` button — never a full `members.map` (which
 * would re-mount the thousands of nodes the cap exists to prevent). No silent
 * truncation: the hidden count is always shown.
 */
function GridCell({
  members,
  colors,
  focus,
  onChipMenu,
  onCellMenu,
}: {
  readonly members: readonly GridMemberVM[];
  readonly colors: ReadonlyMap<string, string>;
  readonly focus?: ReadonlySet<string>;
  readonly onChipMenu: (e: MouseEvent, m: GridMemberVM) => void;
  readonly onCellMenu: (e: MouseEvent) => void;
}): ReactNode {
  const [shown, setShown] = useState(MEMBER_CAP);
  if (members.length === 0) {
    return (
      <td
        data-count={0}
        className="arch-grid-empty"
        aria-label="empty"
        onContextMenu={onCellMenu}
      />
    );
  }
  const visible = shown >= members.length ? members : members.slice(0, shown);
  const hidden = members.length - visible.length;
  return (
    <td data-count={members.length} className="arch-grid-cell" onContextMenu={onCellMenu}>
      <div className="arch-grid-cell-chips">
        {visible.map((m) => {
          const color = colors.get(m.key);
          const ground = isGround(focus, m.key);
          return (
            <span
              key={m.key}
              className="arch-grid-chip"
              title={m.key}
              // Composes with the colour rather than replacing it: the ground
              // recedes, its chip tint stays whatever the legend says it is.
              style={{ ...chipStyle(color), ...(ground ? { opacity: FOCUS_GROUND_ALPHA } : {}) }}
              {...(ground ? { "data-focus": "ground" as const } : {})}
              onContextMenu={(e) => onChipMenu(e, m)}
            >
              {color && <span className="arch-grid-chip-dot" aria-hidden="true" />}
              {m.label}
            </span>
          );
        })}
        {hidden > 0 && (
          <button
            type="button"
            className="arch-grid-more focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
            onClick={() => setShown((s) => s + MEMBER_STEP)}
          >
            +{hidden} more
          </button>
        )}
      </div>
    </td>
  );
}

export function ViewGridView({ data, focus }: ViewGridViewProps): ReactNode {
  const { t } = useTranslation("nav");
  const menu = useContextMenu();

  // The grid is display-only (no cell write target), so every menu is copy —
  // `navigator.clipboard?.writeText` is always safe. Right-click reaches every
  // part of the grid: chips, cells, both header axes, and legend rows.
  const copy = (s: string): void => void navigator.clipboard?.writeText(s);
  const chipMenu = (m: GridMemberVM): readonly ContextMenuItem[] => [
    { label: t("menu.copyName"), onClick: () => copy(m.key) },
  ];
  const labelMenu = (label: string): readonly ContextMenuItem[] => [
    { label: t("menu.copyName"), onClick: () => copy(label) },
  ];
  const cellMenu = (r: string, c: string, members: readonly GridMemberVM[]): readonly ContextMenuItem[] => {
    const items: ContextMenuItem[] = [{ label: t("menu.copyCell"), onClick: () => copy(`${r} · ${c}`) }];
    if (members.length > 0)
      items.push({ label: t("menu.copyMembers"), onClick: () => copy(members.map((m) => m.label).join("\n")) });
    return items;
  };

  // Vertical row windowing (rows can be many; a cross-tab's COLUMNS are aspect
  // values — bounded — so, like the table, columns aren't virtualized). The
  // per-cell member cap (GridCell) is the primary DOM bound: cells are the one
  // unbounded axis of a grid.
  const scrollRef = useRef<HTMLDivElement>(null);
  const rowWin = useWindow(scrollRef, data.axisRows.length, { estimateSize: 44, dynamic: true });
  const spannedCols = data.axisCols.length + 1;

  return (
    <div className="arch-view-grid">
      <div className="arch-grid-scroll" ref={scrollRef}>
        <table className="arch-grid-table" aria-rowcount={rowWin.windowed ? data.axisRows.length : undefined}>
          <thead>
            <tr>
              <th className="arch-grid-corner" />
              {data.axisCols.map((c) => (
                <th
                  key={c}
                  scope="col"
                  className="arch-grid-colhead"
                  title={c}
                  onContextMenu={(e) => menu.open(e, labelMenu(c))}
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <SpacerRow height={rowWin.before} cols={spannedCols} />
            {rowWin.indices.map((ri) => {
              const r = data.axisRows[ri];
              if (r === undefined) return null;
              return (
                <tr
                  key={r}
                  data-index={ri}
                  aria-rowindex={rowWin.windowed ? ri + 1 : undefined}
                  ref={rowWin.measureElement}
                >
                  <th
                    scope="row"
                    className="arch-grid-rowhead"
                    title={r}
                    onContextMenu={(e) => menu.open(e, labelMenu(r))}
                  >
                    {r}
                  </th>
                  {data.axisCols.map((c, ci) => {
                    const members = data.cells[ri]?.[ci]?.members ?? [];
                    return (
                      <GridCell
                        key={ci}
                        members={members}
                        colors={data.colors}
                        focus={focus}
                        onChipMenu={(e, m) => menu.open(e, chipMenu(m))}
                        onCellMenu={(e) => menu.open(e, cellMenu(r, c, members))}
                      />
                    );
                  })}
                </tr>
              );
            })}
            <SpacerRow height={rowWin.after} cols={spannedCols} />
          </tbody>
        </table>
      </div>
      {data.legend.length > 0 && (
        <ul className="arch-grid-legend" aria-label="color legend">
          {data.legend.map((e) => (
            <li
              key={e.label}
              className="arch-grid-legend-row"
              onContextMenu={(ev) => menu.open(ev, labelMenu(e.label))}
            >
              <span
                className="arch-grid-legend-swatch"
                aria-hidden="true"
                style={e.color ? { backgroundColor: paletteColor(e.color) } : undefined}
              />
              {e.label}
            </li>
          ))}
        </ul>
      )}
      {menu.node}
    </div>
  );
}
